import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { CommuniqueVisibilityFlagsResponseDTO, PatientPCCItemsDTO } from 'artifacts/dtos';
import {
  useGetIntakeFlagsByToken,
  useGetPCCItemsByPatientId,
} from 'hooks/useApi';
import useCommuniqueState from 'hooks/useCommuniqueState';
import {
  getPCCPatientId,
  isEmptyString,
} from 'utils/helper';

export type IntakeFlags = CommuniqueVisibilityFlagsResponseDTO & {
  shouldShowTempo?: boolean;
  isTempoEnabledForOrg?: boolean;
  shouldShowRTM?: boolean;
  isRTMEnabledForOrg?: boolean;
  isPCCEnabledForOrg?: boolean;
};

const emptyFlags: IntakeFlags = {
  shouldShowIntake: false,
  shouldShowTempo: false,
  isTempoEnabledForOrg: false,
  shouldShowRTM: false,
  isRTMEnabledForOrg: false,
  isPCCEnabledForOrg: false,
};

export function normalizeIntakeFlags(
  flags?: IntakeFlags | null,
  pccItems?: PatientPCCItemsDTO | null,
): IntakeFlags {
  const isPCC = flags?.isPCCEnabledForOrg ?? false;

  const isTempoPcc = isPCC
    && Boolean(pccItems?.tempo?.isEnabled)
    && !isEmptyString(pccItems?.tempo?.token);
  const isTempoCompletedPcc = isPCC && pccItems?.tempo?.status === 'Completed';

  const isRtmPcc = isPCC
    && Boolean(pccItems?.rtm?.isEnabled)
    && !isEmptyString(pccItems?.rtm?.token);
  const isRtmCompletedPcc = isPCC && pccItems?.rtm?.status === 'Completed';

  return {
    shouldShowIntake: flags?.shouldShowIntake ?? false,
    shouldShowTempo: isPCC
      ? (isTempoPcc && !isTempoCompletedPcc)
      : (flags?.shouldShowTempo ?? false),
    isTempoEnabledForOrg: isPCC
      ? (isTempoPcc || isTempoCompletedPcc)
      : (flags?.isTempoEnabledForOrg ?? false),
    shouldShowRTM: isPCC
      ? (isRtmPcc && !isRtmCompletedPcc)
      : (flags?.shouldShowRTM ?? false),
    isRTMEnabledForOrg: isPCC
      ? (isRtmPcc || isRtmCompletedPcc)
      : (flags?.isRTMEnabledForOrg ?? false),
    isPCCEnabledForOrg: isPCC,
  };
}

type UseIntakeFlagsOptions = {
  intakeFlags?: IntakeFlags | null;
  pccItems?: PatientPCCItemsDTO | null;
};

type UseIntakeFlagsResult = {
  flags: IntakeFlags;
  pccItems: PatientPCCItemsDTO | null;
  isLoading: boolean;
};

export default function useIntakeFlags(
  options?: UseIntakeFlagsOptions,
): UseIntakeFlagsResult {
  const { communiqueData } = useCommuniqueState();
  const { appointmentToken, appointmentPatientId } = useSelector(
    (state: RootState) => state.appStateSettings,
  );
  const patientId = appointmentPatientId || communiqueData?.patientId;
  const token = appointmentToken || communiqueData?.token;
  const hasProvidedFlags = !isNil(options?.intakeFlags);

  const { mutateAsync: getIntakeFlagsByToken } = useGetIntakeFlagsByToken();
  const { mutateAsync: getPCCItems } = useGetPCCItemsByPatientId();

  const [localFlags, setLocalFlags] = useState(emptyFlags);
  const [localPccItems, setLocalPccItems] = useState<PatientPCCItemsDTO | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const resolvedFlags = hasProvidedFlags
    ? normalizeIntakeFlags(options!.intakeFlags, options!.pccItems)
    : localFlags;
  const resolvedPccItems = hasProvidedFlags
    ? (options!.pccItems ?? null)
    : localPccItems;

  const loadFlags = useCallback(async (isCancelled: () => boolean) => {
    setHasLoaded(false);

    let nextFlags = emptyFlags;
    let nextPccItems: PatientPCCItemsDTO | null = null;

    if (!isEmptyString(token)) {
      try {
        const flags = await getIntakeFlagsByToken({ token: token ?? '' });

        if (flags?.isPCCEnabledForOrg) {
          let targetPatientId = patientId;
          if (!targetPatientId && token) {
            try {
              targetPatientId = getPCCPatientId(token);
            } catch {
              // Patient ID fallback ignore
            }
          }
          if (targetPatientId) {
            nextPccItems = await getPCCItems(targetPatientId);
          }
        }

        nextFlags = normalizeIntakeFlags(flags, nextPccItems);
      } catch {
        nextFlags = emptyFlags;
      }
    }

    if (!isCancelled()) {
      setLocalFlags(nextFlags);
      setLocalPccItems(nextPccItems);
      setHasLoaded(true);
    }
  }, [getIntakeFlagsByToken, getPCCItems, patientId, token]);

  useEffect(() => {
    let isCancelled = false;

    if (!hasProvidedFlags) {
      loadFlags(() => isCancelled);
    }

    return () => {
      isCancelled = true;
    };
  }, [hasProvidedFlags, loadFlags]);

  const isLoading = !hasProvidedFlags && !hasLoaded;

  return {
    flags: resolvedFlags,
    pccItems: resolvedPccItems,
    isLoading,
  };
}
